import type { ThemeConfig } from "antd";

/**
 * OpenStore Orion Theme Configuration
 * Based on Figma design specifications and OpenStore brand guidelines
 */
export const orionTheme: ThemeConfig = {
  token: {
    // Primary Colors (From Figma)
    colorPrimary: "#1D4A88", // Primary button color from Figma
    colorPrimaryHover: "#27599E", // Hover state from Figma
    colorPrimaryActive: "#11386C", // Pressed state from Figma
    colorPrimaryBg: "#f0f7ff", // Light background
    colorPrimaryBgHover: "#e0efff", // Light background hover
    colorPrimaryBorder: "#1D4A88", // Border color
    colorPrimaryBorderHover: "#27599E", // Border hover

    // Success Colors
    colorSuccess: "#34a853",
    colorSuccessHover: "#2e7d32",
    colorSuccessActive: "#1b5e20",
    colorSuccessBg: "#f0fdf4",
    colorSuccessBorder: "#34a853",

    // Error Colors
    colorError: "#ea4335",
    colorErrorHover: "#d32f2f",
    colorErrorActive: "#b71c1c",
    colorErrorBg: "#fef2f2",
    colorErrorBorder: "#ea4335",

    // Warning Colors
    colorWarning: "#fbbc04",
    colorWarningHover: "#e8710a",
    colorWarningActive: "#c2410c",
    colorWarningBg: "#fffbeb",
    colorWarningBorder: "#fbbc04",

    // Info Colors (using Orion blue)
    colorInfo: "#1D4A88",
    colorInfoHover: "#27599E",
    colorInfoActive: "#11386C",
    colorInfoBg: "#f0f7ff",
    colorInfoBorder: "#1D4A88",

    // Neutral Colors
    colorTextBase: "#202124", // Primary text
    colorTextSecondary: "#5f6368", // Secondary text
    colorTextTertiary: "#9aa0a6", // Tertiary text
    colorTextQuaternary: "#bdc1c6", // Disabled text

    // Background Colors
    colorBgBase: "#ffffff", // Base background
    colorBgContainer: "#ffffff", // Container background
    colorBgElevated: "#ffffff", // Elevated background
    colorBgLayout: "#fafbfb", // Layout background
    colorBgSpotlight: "#f6f6f7", // Spotlight background
    colorBgMask: "rgba(0, 0, 0, 0.45)", // Mask background

    // Border Colors
    colorBorder: "#e1e3e5", // Default border
    colorBorderSecondary: "#f1f2f3", // Secondary border

    // Typography (From Figma)
    fontFamily:
      'Inter, -apple-system, BlinkMacSystemFont, "San Francisco", "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
    fontSize: 14, // Base font size from Figma
    fontSizeLG: 16, // Large font size from Figma
    fontSizeSM: 12, // Small font size
    fontSizeXL: 18, // Extra large
    fontSizeHeading1: 48, // H1
    fontSizeHeading2: 30, // H2
    fontSizeHeading3: 24, // H3
    fontSizeHeading4: 20, // H4
    fontSizeHeading5: 16, // H5
    fontWeightStrong: 500, // Medium weight from Figma
    lineHeight: 1.4285714285714286, // From Figma
    lineHeightLG: 1.5,
    lineHeightSM: 1.66,

    // Layout & Spacing
    borderRadius: 8, // From Figma (8px)
    borderRadiusLG: 12,
    borderRadiusSM: 4,
    borderRadiusXS: 2,

    // Control Heights (From Figma)
    controlHeight: 40, // Medium button (40px from Figma)
    controlHeightLG: 44, // Large button (44px from Figma)
    controlHeightSM: 36, // Small button (36px from Figma)
    controlHeightXS: 32, // Extra small

    // Padding (From Figma: 10px 16px)
    paddingContentHorizontal: 16, // Horizontal padding from Figma
    paddingContentVertical: 10, // Vertical padding from Figma
    paddingContentHorizontalLG: 20,
    paddingContentHorizontalSM: 12,

    // Shadows (From Figma)
    boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
    boxShadowSecondary:
      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    boxShadowTertiary:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",

    // Motion
    motionDurationFast: "0.1s",
    motionDurationMid: "0.2s",
    motionDurationSlow: "0.3s",
    motionEaseInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
    motionEaseOut: "cubic-bezier(0, 0, 0.2, 1)",
  },

  components: {
    Button: {
      // Button-specific theme overrides
      primaryShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
      defaultShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
      dangerShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",

      // Border radius from Figma
      borderRadius: 8,
      borderRadiusLG: 8,
      borderRadiusSM: 8,

      // Padding from Figma (10px 16px)
      paddingInline: 16,
      paddingInlineLG: 20,
      paddingInlineSM: 12,

      // Disabled state (40% opacity from Figma)
      colorTextDisabled: "rgba(32, 33, 36, 0.4)",
      colorBgContainerDisabled: "rgba(29, 74, 136, 0.4)",
    },

    Card: {
      borderRadius: 8,
      boxShadow:
        "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
      headerBg: "#ffffff",
      bodyPadding: 24,
    },

    Input: {
      borderRadius: 6,
      paddingInline: 12,
      paddingBlock: 8,
      fontSize: 14,
      colorBorder: "#e1e3e5",
      activeBorderColor: "#1D4A88",
    },

    DatePicker: {
      borderRadius: 4,
      paddingInline: 12,
      paddingBlock: 8,
      fontSize: 14,
      colorBorder: "#E6E6E6",
      activeBorderColor: "#1D4A88",
      hoverBorderColor: "#999999",
      colorTextPlaceholder: "#999999",
      colorText: "#333333",
    },

    Select: {
      borderRadius: 6,
      optionPadding: "8px 12px",
      colorBorder: "#e1e3e5",
      activeBorderColor: "#1D4A88",
    },

    Dropdown: {
      borderRadius: 6,
      colorBorder: "#e1e3e5",
    },

    Modal: {
      borderRadius: 12,
      headerBg: "#ffffff",
      contentBg: "#ffffff",
      footerBg: "#fafbfb",
      titleColor: "#202124",
      titleFontSize: 18,
    },

    Form: {
      labelColor: "#202124",
      labelFontSize: 14,
      itemMarginBottom: 16,
      verticalLabelPadding: "0 0 8px",
    },

    Message: {
      borderRadius: 8,
      contentPadding: "12px 16px",
    },

    Notification: {
      borderRadius: 8,
      paddingMD: 16,
    },

    Tooltip: {
      borderRadius: 6,
      colorBgSpotlight: "#202124",
      colorTextLightSolid: "#ffffff",
    },

    Popover: {
      borderRadius: 8,
      boxShadow:
        "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    },

    Drawer: {
      borderRadius: 0,
      colorBgElevated: "#ffffff",
      colorBgMask: "rgba(0, 0, 0, 0.45)",
    },

    Badge: {
      borderRadius: 999,
      fontSize: 12,
      lineHeight: 1.5,
    },

    Tag: {
      borderRadius: 999,
      fontSize: 12,
      lineHeight: 1.5,
    },

    Menu: {
      borderRadius: 6,
      itemBorderRadius: 4,
      colorPrimary: "#1D4A88",
      colorBgElevated: "#ffffff",
      colorItemBgSelected: "#f0f7ff",
      colorItemBgHover: "#f6f6f7",
    },

    Pagination: {
      borderRadius: 2,
      colorText: "rgba(102, 102, 102, 0.85)",
      colorTextDisabled: "#d9d9d9",
    },

    Tabs: {
      borderRadius: 6,
      colorText: "#344054",
      colorTextSecondary: "#666666",
      colorTextDisabled: "#d9d9d9",
      colorBgContainer: "#ffffff",
      colorBorder: "#E6E6E6",
    },

    Checkbox: {
      borderRadius: 4,
      colorText: "#4B4B4B",
      colorTextDisabled: "#d9d9d9",
      colorBgContainer: "#ffffff",
      colorBorder: "#E6E6E6",
    },

    Switch: {
      borderRadius: 16,
      colorText: "#4B4B4B",
      colorTextDisabled: "#d9d9d9",
      colorBgContainer: "rgba(0, 0, 0, 0.25)",
      colorBorder: "transparent",
      controlHeight: 16,
      controlHeightSM: 14,
      controlHeightLG: 18,
    },

    Radio: {
      borderRadius: 50,
      colorText: "#4B4B4B",
      colorTextDisabled: "#d9d9d9",
      colorBgContainer: "#ffffff",
      colorBorder: "#E1E1E1",
    },

    Table: {
      borderRadius: 8,
      headerBg: "#f8f8f8",
      headerColor: "#344054",
      headerSplitColor: "#eaeaea",
      borderColor: "#eaecef",
      rowHoverBg: "#f6f6f7",
      rowSelectedBg: "#f0f7ff",
      rowSelectedHoverBg: "#e0efff",
      colorText: "#344054",
      colorTextSecondary: "#5f6368",
      fontSize: 14,
      fontFamily:
        'Inter, -apple-system, BlinkMacSystemFont, "San Francisco", "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
    },
  },

  // Algorithm for color generation
  algorithm: undefined, // Use default algorithm
};

// Export individual color tokens for direct use
export const orionColors = {
  primary: "#1D4A88",
  primaryHover: "#27599E",
  primaryActive: "#11386C",
  success: "#34a853",
  error: "#ea4335",
  warning: "#fbbc04",
  neutral: {
    0: "#ffffff",
    50: "#fafbfb",
    100: "#f6f6f7",
    200: "#edeeef",
    300: "#e1e3e5",
    400: "#bdc1c6",
    500: "#9aa0a6",
    600: "#5f6368",
    700: "#202124",
    800: "#1a1a1a",
    900: "#0f0f0f",
  },
} as const;

// Export typography tokens
export const orionTypography = {
  fontFamily:
    'Inter, -apple-system, BlinkMacSystemFont, "San Francisco", "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
  fontSize: {
    xs: 12,
    sm: 14,
    base: 16,
    lg: 18,
    xl: 20,
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.4285714285714286, // From Figma
    relaxed: 1.5,
  },
} as const;

// Export spacing tokens
export const orionSpacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
} as const;
